import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';

import CoreLink from './core_link';
import { Filter } from './search_filters';
import { useUser } from '../user_hooks';
import MarketplaceFilter from './marketplace_filter';
import { Location } from 'history';
import { isSomethingSelected } from '../facet_utils';
import { RCChip, RCText, RCIcon, useMediaQuery } from '@reverbdotcom/cadence/components';
import { SlidersHorizontalIcon } from '@reverbdotcom/cadence/icons/react';
import MarketplaceFilterSidebar from './marketplace_filter_sidebar';
import { getExcludedFilterKeys, getFiltersTotal } from './helpers/selected_filters_helper';
import { applyProximityBoost } from '../seller_location_experiments';
import GridRowWithInputChips, { InputChipFilterOption } from './grid_row_with_input_chips';
import { TraitFilter } from './traitFilters';

export interface CSP2FiltersOption {
  name: string;
  total: number;
  selected: boolean;
  optionValue?: string;
  setValues?: any;
  unsetValues?: any;
  paramName?: string;
}
interface IProps {
  filters?: Filter[];
  loading?: boolean;
  trackingQuery?: string;
  displayCount?: boolean;
  dynamicTitle?: boolean;
  hideOnMobile?: boolean;
  location: Location;
  resultsTotal?: number;
  showTraitFilters?: boolean;
  hideClearFiltersButton?: boolean;
  pinTextQuery?: boolean; // when true, RQLSearchOverview is responsible for rendering TEXT_QUERY filter, and it can be omitted here.
  toggleExposedSidebarOpen?: () => void;
  showNewMobileSidebar?: boolean;
  isFilterOpenByDefault?: (filter: Filter) => boolean;
  clearFiltersUrl?: string;
  filterInputChips?: Filter[];
  modifyRemovableFilterChips?: (filterChips: InputChipFilterOption[]) => InputChipFilterOption[];
  hideConditionOptions?: boolean;
  traitFilters?: Filter[];
  showCpsDrawer?: boolean;
  showListViewToggleInPanel?: boolean;
  setListViewState?: (val: boolean) => void;
  filterChipCopyOverride?: string;
}

export const FEATURED = [
  'CONDITION_SLUGS',
  'CATEGORY_SLUGS',
  'BRAND_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
  'LOCATION',
];

export const MODEL_FAMILY_FEATURED = [
  'CONDITION_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
];

const CSP_FEATURED = [
  'CONDITION_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
  '6a24e2dc-800d-4f06-ac67-acef9569a40f', // fretboard material filter
];

const ALWAYS_SHOW_SELLER_LOCATION_FILTER_REGIONS = [
  'GB',
  'AU',
];

export function isDefaultedSellerLocationFilter(filterKey, shippingRegionCode) {
  return filterKey === 'SELLER_LOCATION' && ALWAYS_SHOW_SELLER_LOCATION_FILTER_REGIONS.includes(shippingRegionCode);
}

export default function MarketplaceFilterRow({
  filters = [],
  loading = false,
  trackingQuery = '',
  displayCount = false,
  dynamicTitle = false,
  hideOnMobile = false,
  location = null,
  resultsTotal = null,
  showTraitFilters = false, // show the Finish and Fretboard Material Filters
  hideClearFiltersButton = false,
  pinTextQuery = false,
  showNewMobileSidebar = false,
  toggleExposedSidebarOpen = undefined,
  isFilterOpenByDefault = undefined,
  clearFiltersUrl = undefined,
  filterInputChips = [],
  modifyRemovableFilterChips = undefined,
  traitFilters = [{ options: [] }, { options: [] }],
  showCpsDrawer = false,
  showListViewToggleInPanel = false,
  setListViewState = undefined,
  filterChipCopyOverride = undefined,
}: IProps) {
  const user = useUser();
  const pathname = location?.pathname;
  const excludedFilterKeys = getExcludedFilterKeys(pathname, pinTextQuery);
  const isMobile = useMediaQuery('mobile');

  if (loading && filters.length === 0) {
    return (
      <div className="search-overview__filters">
        <Placeholder />
      </div>
    );
  }

  const includedFilters = filters.filter(f => {
    const key = getFilterKey(f);
    return !excludedFilterKeys.includes(key);
  });

  const defaultFilters = getFilters(includedFilters, user, pathname);

  const defaultFilterKeys = defaultFilters.map(f => {
    return f.aggregationName ? f.aggregationName : f.key;
  });

  const otherSelectedFilters = includedFilters.filter(f => {
    const key = getFilterKey(f);
    if (defaultFilterKeys.includes(key)) return false;
    return f.options && f.options.some(o => {
      if (o.all) return false;
      // Note - we hide the SHIPS_TO filter only if you've selected your default region. It's additional noise at that point
      if (key === 'SHIPS_TO' && o.optionValue === user.shippingRegionCode) return false;
      return o.selected;
    });
  });

  const allSelectedFilters = includedFilters.filter(f => {
    const key = f.aggregationName || f.key;

    // Note - we hide the SHIPS_TO filter only if you've selected your default region. It's additional noise at that point
    if (key === 'SHIPS_TO') {
      if (f.options.some(o => {
        return o.optionValue === user.shippingRegionCode;
      })) return false;
      return true;
    }
    return isSomethingSelected(f, true);
  });
  const [finishFilter] = traitFilters;
  const filtersForSidebar = showTraitFilters ? [...traitFilters, ...filters] : filters;
  const selectedFiltersTotal = getFiltersTotal(filtersForSidebar, excludedFilterKeys, user.shippingRegionCode);
  const noFiltersSelected = showCpsDrawer ? (selectedFiltersTotal > 1) : (selectedFiltersTotal > 0);

  if (isSomethingSelected(finishFilter, true)) {
    allSelectedFilters.push(finishFilter);
  }

  return (
    <>
      <div className="search-overview__filters search-overview__filters--mobile">
        <MarketplaceFilterSidebar
          filters={filtersForSidebar}
          loading={loading}
          resultsTotal={resultsTotal}
          trackingQuery={trackingQuery}
          location={location}
          isFilterOpenByDefault={isFilterOpenByDefault}
          toggleExposedSidebarOpen={toggleExposedSidebarOpen}
          hideSidebarOnMobile={showNewMobileSidebar}
          clearFiltersUrl={clearFiltersUrl}
          hideClearFiltersButton={hideClearFiltersButton}
          showListViewToggleInPanel={showListViewToggleInPanel}
          setListViewState={setListViewState}
          filterChipCopyOverride={filterChipCopyOverride}
        />
        {defaultFilters.map(f => {
          return (
            <MarketplaceFilter
              filter={f}
              filters={filters}
              loading={loading}
              key={f.name}
              trackingQuery={trackingQuery}
              dynamicTitle={dynamicTitle}
              displayCount={displayCount}
              hideOnMobile={hideOnMobile}
              hideConditionOptions={showCpsDrawer}
            />
          );
        })}
        {dynamicTitle && otherSelectedFilters.map(f => {
          return (
            <MarketplaceFilter
              filter={f}
              filters={filters}
              loading={loading}
              key={f.name}
              trackingQuery={trackingQuery}
              dynamicTitle={dynamicTitle}
              displayCount={displayCount}
              hideOnMobile={hideOnMobile}
            />
          );
        })}
        {showTraitFilters && (traitFilters.map((filter, idx) =>
          <TraitFilter
            key={idx}
            loading={loading}
            filter={filter}
            dynamicTitle
            showOnMobile={showCpsDrawer ? true : false}
            displayOnlyIfSelected={showCpsDrawer && isMobile ? true : false}
          />,
        ))}
        {noFiltersSelected && !hideClearFiltersButton &&
          <ClearFiltersButton
            clearFilterUrl={clearFiltersUrl || pathname}
            hideOnMobile={showCpsDrawer ? false : true}
          />
        }
        {showNewMobileSidebar &&
          <div className="mobile-d-inline d-none">
            <RCChip
              loading={loading}
              count={selectedFiltersTotal}
              onClick={toggleExposedSidebarOpen}>
              <RCIcon icon={<SlidersHorizontalIcon />} size="inline-prominent" spacing="right" />
              {noFiltersSelected ? (
                <span className="visually-hidden">
                  {I18n.t('commons.marketplaceFilters.filterAndSort')}
                </span>
              ) : I18n.t('commons.marketplaceFilters.filterAndSort')}
            </RCChip>
          </div>
        }
        <div className="d-none mobile-d-block">
          <GridRowWithInputChips
            filters={filterInputChips}
            modifyRemovableFilterChips={modifyRemovableFilterChips}
          />
        </div>
      </div>
    </>
  );
}

function Placeholder() {
  return (
    <div className="marketplace-filter">
      <RCChip.Placeholder />
    </div>
  );
}

function getFilterKey(f: Filter) {
  return f.key || f.aggregationName;
}

function getFeaturedFilterKeys(pathname) {
  if (pathname?.includes('/f/')) return MODEL_FAMILY_FEATURED;
  if (pathname?.includes('/p/')) return CSP_FEATURED;
  if (pathname?.includes('/sell/search')) return CSP_FEATURED;
  return FEATURED;
}

function getFilters(filters, user, pathname) {
  const featuredFilterKeys = getFeaturedFilterKeys(pathname);
  const featuredFilters = filters.filter(f => {
    const key = getFilterKey(f);
    if (key === 'LOCATION' && user.shippingRegionCode !== 'US_CON') return false;
    if (key === 'SELLER_LOCATION' && pathname?.includes('/p/') && applyProximityBoost(user)) return true;
    return featuredFilterKeys.includes(key) || isDefaultedSellerLocationFilter(key, user.shippingRegionCode);
  });
  const hasFeaturedFilters = featuredFilters.length > 0;

  const selectedFilters = hasFeaturedFilters ? featuredFilters : filters.slice(0, 5);
  const mergedFilters = mergeFilterOptions(selectedFilters, filters, 'LOCATION', 'PICKUP');

  if (!hasFeaturedFilters) {
    return mergedFilters;
  }

  return mergedFilters.sort((a: Filter, b: Filter) => {
    const keyA = getFilterKey(a);
    const keyB = getFilterKey(b);
    const indexOfA = featuredFilterKeys.indexOf(keyA);
    const indexOfB = featuredFilterKeys.indexOf(keyB);
    if (indexOfA < indexOfB) { return -1; }
    if (indexOfA > indexOfB) { return 1; }
    return 0;
  });
}

function mergeFilterOptions(targetFilters: Filter[], sourceFilters: Filter[], targetKey: string, sourceKey: string) {
  const targetFilterIdx = targetFilters.findIndex(f => getFilterKey(f) === targetKey);
  const targetFilter = cloneDeep(targetFilters[targetFilterIdx]);
  const sourceFilter = sourceFilters.find(f => getFilterKey(f) === sourceKey);

  if (targetFilter && sourceFilter) {
    targetFilter.options.push(...sourceFilter.options);
  }

  targetFilters[targetFilterIdx] = targetFilter;

  return targetFilters;
}

export function ClearFiltersButton({ clearFilterUrl, hideOnMobile = false, hideOnDesktop = false }) {
  const className = classNames(
    'marketplace-filter',
    {
      'mobile-d-none': hideOnMobile,
      'd-none mobile-d-inline': hideOnDesktop,
    },
  );

  return (
    <div className={className}>
      <CoreLink
        to={clearFilterUrl}
        className="text-link plr-2 d-flex height-100 fx-align-center"
      >
        <RCText.Utility size="350" weight="semibold" htmlTag="span">
          {I18n.t('commons.marketplaceFilters.clearFilters')}
        </RCText.Utility>
      </CoreLink>
    </div>
  );
}
